import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { resolveOriginalMoney } from "../../core/currency";

dayjs.extend(utc);
dayjs.extend(timezone);

const UK_TZ = "Europe/London";

// ── Currency rule for every aggregate in this file ──────────────────────────
// The figures these cards add up are NOT all in the same currency:
//   · order.total_amount / item.price  → the ORDER currency (order.currency)
//   · the listing's buying price       → listing.buying_currency
//                                        (gbp_buying_price is only the legacy
//                                        GBP pivot for rows with no currency)
//   · request budgets                  → the REQUEST currency
// So `profit = sell − buy` and any cross-order total are only meaningful once
// every term has been converted into ONE currency. Callers pass `toBase`,
// which converts (amount, fromCurrency) into the store base and returns null
// when a rate leg is unknown (see crossRate's contract) — a row that cannot be
// converted is EXCLUDED from the aggregate rather than summed at face value.
// An under-counted card is recoverable; a silently wrong one is not.
//
// Buying prices always resolve through resolveOriginalMoney, so the amount the
// rep typed is used with its own currency; the gbp_* pivot is a fallback for
// legacy rows only, and is never scaled into a display currency (that is the
// double-conversion bug: 1000 USD → gbp 747 → 993 USD).
//
// Default: no FX available ⇒ treat every amount as already denominated in the
// base, which is exactly the legacy single-currency behaviour.
const SAME_CURRENCY = (amount) => Number(amount) || 0;

/** Listing buying price in ITS OWN currency, converted into the base. */
const buyingPriceInBase = (listing, toBase) => {
  const money = resolveOriginalMoney(listing, "buying_price", "buying_currency");
  if (!money) return null;
  return toBase(money.amount, money.currency);
};

// ── Client Portfolio Allocation ─────────────────────

export const computePortfolioAllocation = (contacts, admins) => {
  const repCounts = {};
  (admins || []).forEach((a) => {
    repCounts[a.id] = { name: a.full_name || `${a.first_name || ""} ${a.last_name || ""}`.trim(), count: 0 };
  });

  let total = 0;
  (contacts || []).forEach((c) => {
    total++;
    const ownerId = c.owner_id || c.user?.owner_id;
    if (ownerId && repCounts[ownerId]) {
      repCounts[ownerId].count++;
    }
  });

  return Object.values(repCounts)
    .map((r) => ({ ...r, pct: total > 0 ? (r.count / total) * 100 : 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
};

// ── Top Performing Products (by revenue) ────────────

export const computeTopProducts = (orders, limit = 10, toBase = SAME_CURRENCY) => {
  const productMap = {};

  (orders || []).forEach((order) => {
    if (!["paid", "dispatched", "delivered"].includes(order.status)) return;
    const items = order.order_items || order.items || [];
    const orderCurrency = order.currency || null;

    items.forEach((item) => {
      const listing = item.listing || {};
      const product = listing.product || {};
      const key = product.id || listing.id || "unknown";
      const brand = product.brand?.name || "";
      const model = product.model?.name || "";
      const ref = product.reference_number || "";
      const label = [brand, model, ref].filter(Boolean).join(" ") || "Unknown";

      // Sale price is in the ORDER currency — convert before it joins a
      // cross-order total. Unknown rate ⇒ skip the row entirely.
      const sellPrice = toBase(item.price || order.total_amount || 0, orderCurrency);
      if (sellPrice == null) return;

      if (!productMap[key]) productMap[key] = { label, revenue: 0, count: 0, profit: 0 };
      productMap[key].revenue += sellPrice / 100;
      productMap[key].count++;

      // Buying price is in the LISTING's currency, not the order's — both are
      // now in the base, so the subtraction is currency-consistent.
      const buyPrice = buyingPriceInBase(listing, toBase);
      if (buyPrice != null && buyPrice > 0) {
        productMap[key].profit += (sellPrice - buyPrice) / 100;
      }
    });
  });

  return Object.values(productMap)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
};

// ── Top Product by Profit ───────────────────────────

export const computeTopProductByProfit = (orders, limit = 10, toBase = SAME_CURRENCY) => {
  const products = computeTopProducts(orders, 100, toBase);
  return products
    .filter((p) => p.profit > 0)
    .sort((a, b) => b.profit - a.profit)
    .slice(0, limit);
};

// ── Top Brands by Volume ────────────────────────────

export const computeTopBrands = (orders, limit = 10, toBase = SAME_CURRENCY) => {
  const brandMap = {};

  (orders || []).forEach((order) => {
    if (!["paid", "dispatched", "delivered"].includes(order.status)) return;
    const items = order.order_items || order.items || [];
    const orderCurrency = order.currency || null;

    items.forEach((item) => {
      const brand = item.listing?.product?.brand?.name || "Unknown";
      if (!brandMap[brand]) brandMap[brand] = { name: brand, count: 0, revenue: 0 };
      brandMap[brand].count++;
      // Unit counts are currency-free, so an unconvertible row still counts —
      // only its revenue is left out of the cross-order total.
      const sellPrice = toBase(item.price || order.total_amount || 0, orderCurrency);
      if (sellPrice != null) brandMap[brand].revenue += sellPrice / 100;
    });
  });

  return Object.values(brandMap).sort((a, b) => b.count - a.count).slice(0, limit);
};

// ── Top Model Families by Volume ────────────────────

export const computeTopModels = (orders, limit = 10) => {
  const modelMap = {};

  (orders || []).forEach((order) => {
    if (!["paid", "dispatched", "delivered"].includes(order.status)) return;
    const items = order.order_items || order.items || [];

    items.forEach((item) => {
      const product = item.listing?.product || {};
      const brand = product.brand?.name || "";
      const model = product.model?.name || "Unknown";
      const key = `${brand} ${model}`.trim();
      if (!modelMap[key]) modelMap[key] = { name: key, count: 0 };
      modelMap[key].count++;
    });
  });

  return Object.values(modelMap).sort((a, b) => b.count - a.count).slice(0, limit);
};

// ── Total Gross Margin ──────────────────────────────

export const computeGrossMargin = (orders, toBase = SAME_CURRENCY) => {
  let totalRevenue = 0;
  let totalCost = 0;

  (orders || []).forEach((order) => {
    if (!["paid", "dispatched", "delivered"].includes(order.status)) return;
    const items = order.order_items || order.items || [];
    const orderCurrency = order.currency || null;

    items.forEach((item) => {
      // Revenue is order-currency, cost is listing-currency: both go into the
      // base before either total is touched, so `margin` no longer divides one
      // currency by another.
      const sell = toBase(item.price || order.total_amount || 0, orderCurrency);
      if (sell == null) return;
      const buy = buyingPriceInBase(item.listing || {}, toBase);
      totalRevenue += sell / 100;
      if (buy != null && buy > 0) totalCost += buy / 100;
    });
  });

  const margin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0;
  return { totalRevenue, totalCost, profit: totalRevenue - totalCost, margin };
};

// ── At-Risk Revenue ─────────────────────────────────

export const computeAtRiskRevenue = (
  requests,
  inboundMessages,
  outboundMessages,
  toBase = SAME_CURRENCY
) => {
  const fourHoursAgo = dayjs().subtract(4, "hour").unix();
  const repliedChats = new Set((outboundMessages || []).map((m) => m.chat_title));

  // Find inbound direct messages >4h old with no reply
  const staleChats = {};
  (inboundMessages || []).forEach((msg) => {
    if (msg.is_group) return;
    if (repliedChats.has(msg.chat_title)) return;
    if (msg.timestamp > fourHoursAgo) return;

    if (!staleChats[msg.chat_title] || msg.timestamp > staleChats[msg.chat_title].timestamp) {
      staleChats[msg.chat_title] = msg;
    }
  });

  // Match to requests to estimate at-risk revenue
  const atRisk = Object.values(staleChats).map((msg) => {
    const phone = msg.sender?.phone || msg.user_phone;
    const matchingReqs = (requests || []).filter((r) => {
      const clientPhone = r.client?.phone;
      return clientPhone && phone && clientPhone.includes(phone.slice(-8));
    });

    // Budgets are denominated in the REQUEST's own currency, so a multi-client
    // total has to convert each one first (unconvertible rows are left out).
    const estimatedValue = matchingReqs.reduce((sum, r) => {
      const value = toBase(r.quoted_price || r.max_budget || 0, r.currency || null);
      return value == null ? sum : sum + value / 100;
    }, 0);

    return {
      clientName: msg.sender?.wa_name || phone || "Unknown",
      phone,
      waitingHours: Math.round((dayjs().unix() - msg.timestamp) / 3600),
      estimatedValue,
      requestCount: matchingReqs.length,
    };
  });

  return atRisk.sort((a, b) => b.waitingHours - a.waitingHours);
};

// ── Daily Heat Map ──────────────────────────────────

export const computeHeatMap = (inboundMessages) => {
  const hours = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    count: 0,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  (inboundMessages || []).forEach((msg) => {
    // Convert to UK time
    const ukHour = dayjs.unix(msg.timestamp).tz(UK_TZ).hour();
    hours[ukHour].count++;
  });

  const total = hours.reduce((sum, h) => sum + h.count, 0);
  const max = Math.max(...hours.map((h) => h.count), 1);
  hours.forEach((h) => {
    h.intensity = h.count / max;
    h.pct = total > 0 ? ((h.count / total) * 100).toFixed(1) : 0;
  });

  return hours;
};

// ── Top Missed Products ─────────────────────────────
// Looks for product references/names in WA messages that don't match active listings

export const computeMissedProducts = (inboundMessages, limit = 10) => {
  // Watch reference patterns: must contain both letters and numbers, or have separators
  // Examples: 126710BLRO, 5167R-001, 26240ST.OO.1320ST.06, PAM01424, WSSA0029
  const refPattern = /\b([A-Z]{2,}\d{3,}[A-Z0-9]*|[0-9]{5,}[A-Z][A-Z0-9-]*|\d{3,}[A-Z]+[-.]?\d+[A-Z]*[-.]?\d*)\b/gi;
  const refCounts = {};

  // Years and common noise to exclude
  const yearPattern = /^(19|20)\d{2}$/;
  const noiseWords = new Set(["HKD", "USD", "GBP", "EUR", "USED", "NEW", "FULL", "SET"]);

  (inboundMessages || []).forEach((msg) => {
    if (!msg.message_text) return;
    const matches = msg.message_text.match(refPattern);
    if (!matches) return;

    matches.forEach((ref) => {
      const cleaned = ref.trim().toUpperCase();
      if (cleaned.length < 5) return;
      if (yearPattern.test(cleaned)) return;  // skip years
      if (noiseWords.has(cleaned)) return;     // skip common words
      // Must contain at least one letter AND one digit
      if (!/[A-Z]/.test(cleaned) || !/\d/.test(cleaned)) return;
      if (!refCounts[cleaned]) refCounts[cleaned] = { ref: cleaned, count: 0 };
      refCounts[cleaned].count++;
    });
  });

  return Object.values(refCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};
